import { Box, TextField, Typography } from '@mui/material';
import { QueryStatus } from '@reduxjs/toolkit/query';
import React from 'react';
import { Outlet, useNavigate, useParams } from 'react-router';

import {
  useDeleteFiatAccountMutation,
  useGetFiatAccountQuery,
  useUpdateFiatAccountMutation,
} from '@/api/v1';
import { ConfirmationModal } from '@/components/molecules/ConfirmationModal';
import { EditableValue } from '@/components/molecules/EditableValue';
import { LoadingButton } from '@/components/molecules/LoadingButton';
import countries from '@/data/countries';
import { useToast } from '@/hooks/useToast';
import { useTranslator } from '@/hooks/useTranslator';
import { FiatAccount, SimplifiedAccount, UpdateFiatAccountRequest } from '@/types/contacts';

export const AccountDetailsContent: React.FC = (): React.ReactElement => {
  const { accountId = '' } = useParams();

  const navigate = useNavigate();
  const tr = useTranslator();
  const toast = useToast();

  const { data: account = FiatAccount.empty() } = useGetFiatAccountQuery(accountId);

  const [updateFiatAccount, updateFiatAccountMutation] = useUpdateFiatAccountMutation();
  const [deleteFiatAccount, deleteFiatAccountMutation] = useDeleteFiatAccountMutation();
  const [deleteConfirm, setDeleteConfirm] = React.useState<boolean>(false);

  React.useEffect((): void => {
    switch (deleteFiatAccountMutation.status) {
      case QueryStatus.rejected:
        toast.show(tr('AN_ERROR_OCCURRED'), 'error');
        break;
      case QueryStatus.fulfilled:
        toast.show(tr('DELETE_PAYMENT_METHOD_SUCCESS'), 'success');
        navigate('/contacts');
        break;
      default:
        // Ignore
        break;
    }
  }, [deleteFiatAccountMutation.status, navigate, toast, tr]);

  React.useEffect((): void => {
    switch (updateFiatAccountMutation.status) {
      case QueryStatus.rejected:
        toast.show(tr('AN_ERROR_OCCURRED'), 'error');
        break;
      case QueryStatus.fulfilled:
        toast.show(tr('UPDATE_PAYMENT_METHOD_SUCCESS'), 'success');
        break;
      default:
        // Ignore
        break;
    }
  }, [updateFiatAccountMutation.status, toast, tr]);

  const onDelete = React.useCallback((): void => {
    setDeleteConfirm(true);
  }, []);

  const onDeleteAborted = React.useCallback((): void => {
    setDeleteConfirm(false);
  }, []);

  const onDeleteConfirmed = React.useCallback((): void => {
    if (accountId) {
      deleteFiatAccount(accountId);
    }
  }, [deleteFiatAccount, accountId]);

  const simpleAccount = React.useMemo(
    (): SimplifiedAccount => FiatAccount.toSimplifiedAccount(account),
    [account],
  );

  const country = React.useMemo(
    (): (typeof countries)[0] | undefined =>
      countries.find(
        (country: (typeof countries)[0]): boolean => country.value === simpleAccount?.country,
      ),
    [simpleAccount],
  );

  const onUpdateNickname = React.useCallback(
    async (value: string): Promise<void> => {
      const request: UpdateFiatAccountRequest = {
        id: accountId,
        data: {
          name: value,
        },
      };

      updateFiatAccount(request);
    },
    [accountId, updateFiatAccount],
  );

  const onValidateNickname = React.useCallback((value: string): string | null => {
    if (value.trim() === '') {
      return 'NICKNAME_REQUIRED_ERROR';
    }

    return null;
  }, []);

  return (
    <Box px={6} mt={4}>
      <Typography fontSize={20} fontWeight={500} lineHeight="32px">
        {tr('ACCOUNT_DETAILS')}
      </Typography>
      <Box display="flex" flexDirection="column" gap={4} mt={3}>
        <Box display="flex" flexDirection="column" gap={0.5}>
          {simpleAccount.bankName ? (
            <Typography variant="body2" color="text.primary">
              {simpleAccount.bankName}
            </Typography>
          ) : null}
          <Typography variant="body2" color="text.primary">
            {tr(country?.label ?? '')}
          </Typography>

          <EditableValue
            label={tr('NICKNAME')}
            value={account.name}
            displayValue={account.name}
            input={TextField}
            onValidate={onValidateNickname}
            onUpdate={onUpdateNickname}
          />
        </Box>
        <LoadingButton
          variant="contained"
          color="error"
          fullWidth={true}
          loading={deleteFiatAccountMutation.isLoading}
          onClick={onDelete}>
          {tr('DELETE_ACCOUNT')}
        </LoadingButton>
      </Box>

      {/* Simpler modals */}
      <ConfirmationModal
        open={deleteConfirm}
        confirmButtonText={tr('DELETE_ACCOUNT')}
        abortButtonText={tr('KEEP_ACCOUNT')}
        title={tr('ARE_YOU_SURE_YOU_WANT_TO_DELETE_THIS_ACCOUNT_TITLE')}
        description={tr('THIS_ACTION_IS_NOT_REVERSIBLE')}
        onAbort={onDeleteAborted}
        onConfirm={onDeleteConfirmed}
      />

      {/* For modals */}
      <Outlet />
    </Box>
  );
};
